import {
  IconAddress,
  IconBlocksAndArrows,
  IconBuildCircleOutlineRounded,
  IconBuilding,
  IconBuildingHook,
  IconBuildingWarehouse,
  IconCalendar,
  IconChartArea,
  IconChevronDown,
  IconCompass,
  IconCoordinate,
  IconDollarOutline,
  IconFlag,
  IconFloorPlanSolid,
  IconInvestor,
  IconMap,
  IconMoney,
  IconNote,
  IconNumberSymbolSquare,
  IconProjectOutline,
  IconStairs,
  IconStar,
  IconSwing,
  IconTunnel,
  IconTypeHierarchySub,
  IconVectorSquareClose,
  IconWarehouse,
} from 'components/icons';
import { useTranslation } from 'hooks';
import { LatLngTuple } from 'leaflet';
import { isEmpty, join, lowerFirst, map, reverse, slice, toString } from 'lodash';
import Link from 'next/link';
import { DetailProjectPageContext } from 'pages/du-an/[chi-tiet-du-an]';
import { useContext, useState } from 'react';
import {
  convertDecimalDegreesToDegreesMinutesSeconds,
  convertNumberToVietnameseCurrencyLongString,
} from 'utils';

type ProjectFieldsType = { [key: string]: string[] };
type ProjectInformationType = {
  [key: string]: {
    icon?: JSX.Element;
    content?: string;
    className?: string;
    suffix?: string;
    extendElement?: JSX.Element;
  };
};

const MainContent = () => {
  const { project } = useContext(DetailProjectPageContext).pageData ?? {};
  const { project: projectDetail, expectedPrice } = project ?? {};
  const {
    projectType,
    country,
    fullAddress,
    map: coordinates,
    projectName,
    projectArea,
    constructionProjectArea,
    projectScale,
    complexProjectType,
    totalBlock,
    floorCountPerBlockRange,
    localProjectUtilities,
    ownershipTime,
    startYear,
    expectedCompletionYear,
    investorName,
    constructionGeneralContractor,
    projectOverview,
    investment,
    standardStar,
    typeAreas,
    totalZone,
    totalBackground,
    floorCanBuild,
    directions,
    constructionForm,
    totalWarehouse,
    totalWorkshops,
    floorRange,
    totalBasements,
    totalAreaBuild,
  } = projectDetail ?? {};
  const translate = useTranslation();
  const [isExpandAccordion, setExpandAccordion] = useState(true);
  const projectTypeFields: ProjectFieldsType = {
    complex: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'projectArea',
      'constructionProjectArea',
      'projectScale',
      'complexProjectType',
      'totalBlock',
      'floorCountPerBlockRange',
      'localProjectUtilities',
      'ownershipTime',
      'startYear',
      'expectedCompletionYear',
      'investorName',
      'constructionGeneralContractor',
      'projectOverview',
      'investment',
      'standardStar',
    ],
    landProject: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'investorName',
      'constructionGeneralContractor',
      'projectArea',
      'projectOverview',
      'typeAreas',
      'totalZone',
      'totalBackground',
      'floorCanBuild',
      'directions',
      'constructionForm',
      'projectScale',
      'investment',
    ],
    industrialArea: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'investorName',
      'constructionGeneralContractor',
      'projectArea',
      'constructionProjectArea',
      'projectOverview',
      'totalZone',
      'totalWarehouse',
      'totalWorkshops',
      'floorRange',
      'projectScale',
      'localProjectUtilities',
      'ownershipTime',
      'startYear',
      'expectedCompletionYear',
      'investment',
    ],
    buildingProject: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'projectOverview',
      'investorName',
      'constructionGeneralContractor',
      'projectArea',
      'constructionProjectArea',
      'floorRange',
      'totalBlock',
      'totalBasements',
      'projectScale',
      'localProjectUtilities',
      'ownershipTime',
      'startYear',
      'expectedCompletionYear',
      'investment',
    ],
    shoppingMall: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'projectOverview',
      'investorName',
      'constructionGeneralContractor',
      'projectArea',
      'constructionProjectArea',
      'floorRange',
      'totalBlock',
      'totalAreaBuild',
      'totalBasements',
      'localProjectUtilities',
      'projectScale',
      'ownershipTime',
      'startYear',
      'expectedCompletionYear',
      'investment',
    ],
    socialHouse: [
      'projectType',
      'expectedPrice',
      'country',
      'fullAddress',
      'coordinates',
      'projectName',
      'projectArea',
      'constructionProjectArea',
      'projectScale',
      'complexProjectType',
      'totalBlock',
      'floorCountPerBlockRange',
      'localProjectUtilities',
      'ownershipTime',
      'startYear',
      'expectedCompletionYear',
      'investorName',
      'constructionGeneralContractor',
      'projectOverview',
      'investment',
      'standardStar',
    ],
  };
  const projectInformation: ProjectInformationType = {
    projectType: {
      icon: <IconBuilding className='h-[16px] w-[16px]' />,
      content: projectType
        ? `Bán ${lowerFirst((translate.major.projectType as any)[toString(projectType)])}`
        : undefined,
    },
    expectedPrice: {
      icon: <IconDollarOutline className='h-[16px] w-[16px]' />,
      content:
        expectedPrice?.from && expectedPrice.to
          ? `${convertNumberToVietnameseCurrencyLongString(
              expectedPrice.from,
            )} - ${convertNumberToVietnameseCurrencyLongString(expectedPrice.to)}`
          : expectedPrice?.from
          ? `${convertNumberToVietnameseCurrencyLongString(expectedPrice.from)}`
          : expectedPrice?.to
          ? `${convertNumberToVietnameseCurrencyLongString(expectedPrice.to)}`
          : undefined,
      className: 'text-alert font-[600]',
      suffix: 'VND/m\u00B2',
    },
    country: {
      icon: <IconFlag className='h-[16px] w-[16px]' />,
      content: country?.name,
    },
    fullAddress: {
      icon: <IconAddress className='h-[16px] w-[16px]' />,
      content: fullAddress,
    },
    coordinates: {
      icon: <IconCoordinate className='h-[16px] w-[16px]' />,
      content: coordinates?.coordinates
        ? convertDecimalDegreesToDegreesMinutesSeconds(coordinates?.coordinates as LatLngTuple)
        : undefined,
      extendElement: (
        <Link
          href={`https://maps.google.com/?q=${
            toString(reverse(slice(coordinates?.coordinates as LatLngTuple))) ?? ''
          }`}
          target='_blank'
          rel='noopener noreferrer'
          className='font-[600] leading-[22px] text-primary underline'
        >
          Xem trên Google map
        </Link>
      ),
    },
    projectName: {
      icon: <IconProjectOutline className='h-[16px] w-[16px]' />,
      content: projectName,
    },
    projectArea: {
      icon: <IconVectorSquareClose className='h-[16px] w-[16px]' />,
      content: projectArea?.area ? `${(projectArea?.area).toLocaleString()}` : undefined,
      suffix: 'm\u00B2',
    },
    constructionProjectArea: {
      icon: <IconVectorSquareClose className='h-[16px] w-[16px]' />,
      content: constructionProjectArea?.area
        ? `${(constructionProjectArea?.area).toLocaleString()}`
        : undefined,
      suffix: 'm\u00B2',
    },
    projectScale: {
      icon: <IconNumberSymbolSquare className='h-[16px] w-[16px]' />,
      content: projectScale,
    },
    complexProjectType: {
      icon: <IconTypeHierarchySub className='h-[16px] w-[16px]' />,
      content: isEmpty(complexProjectType)
        ? undefined
        : join(
            map(complexProjectType, (type) => (translate.major.complexProjectType as any)[type]),
            ', ',
          ),
    },
    totalBlock: {
      icon: <IconBlocksAndArrows className='h-[16px] w-[16px]' />,
      content: toString(totalBlock),
    },
    floorCountPerBlockRange: {
      icon: <IconStairs className='h-[16px] w-[16px]' />,
      content:
        floorCountPerBlockRange?.from && floorCountPerBlockRange.to
          ? `${floorCountPerBlockRange.from} - ${floorCountPerBlockRange.to}`
          : floorCountPerBlockRange?.from
          ? `${floorCountPerBlockRange.from}`
          : floorCountPerBlockRange?.to
          ? `${floorCountPerBlockRange.to}`
          : undefined,
    },
    localProjectUtilities: {
      icon: <IconSwing className='h-[16px] w-[16px]' />,
      content: localProjectUtilities,
    },
    ownershipTime: {
      icon: <IconCalendar className='h-[16px] w-[16px]' />,
      content: ownershipTime,
    },
    startYear: {
      icon: <IconCalendar className='h-[16px] w-[16px]' />,
      content: toString(startYear),
    },
    expectedCompletionYear: {
      icon: <IconCalendar className='h-[16px] w-[16px]' />,
      content: toString(expectedCompletionYear),
    },
    investorName: {
      icon: <IconInvestor className='h-[16px] w-[16px]' />,
      content: investorName,
    },
    constructionGeneralContractor: {
      icon: <IconBuildCircleOutlineRounded className='h-[16px] w-[16px]' />,
      content: constructionGeneralContractor,
    },
    projectOverview: {
      icon: <IconMap className='h-[16px] w-[16px]' />,
      content: projectOverview,
    },
    investment: {
      icon: <IconMoney className='h-[16px] w-[16px]' />,
      content:
        investment?.from && investment.to
          ? `${convertNumberToVietnameseCurrencyLongString(
              investment.from,
            )} - ${convertNumberToVietnameseCurrencyLongString(investment.to)}`
          : investment?.from
          ? `${convertNumberToVietnameseCurrencyLongString(investment.from)}`
          : investment?.to
          ? `${convertNumberToVietnameseCurrencyLongString(investment.to)}`
          : undefined,
      className: 'text-alert font-[600]',
      suffix: 'VND/m\u00B2',
    },
    standardStar: {
      icon: <IconStar className='h-[16px] w-[16px]' />,
      content: toString(standardStar),
    },
    typeAreas: {
      icon: <IconVectorSquareClose className='h-[16px] w-[16px]' />,
      content: join(
        map(typeAreas, (typeArea) => `${typeArea.area}m\u00B2`),
        ', ',
      ),
    },
    totalZone: {
      icon: <IconChartArea className='h-[16px] w-[16px]' />,
      content: toString(totalZone),
    },
    totalBackground: {
      icon: <IconFloorPlanSolid className='h-[16px] w-[16px]' />,
      content: toString(totalBackground),
    },
    floorCanBuild: {
      icon: <IconStairs className='h-[16px] w-[16px]' />,
      content: toString(floorCanBuild),
    },
    directions: {
      icon: <IconCompass className='h-[16px] w-[16px]' />,
      content: isEmpty(directions)
        ? undefined
        : join(
            map(directions, (direction) => (translate.major.direction as any)[direction]),
            ', ',
          ),
    },
    constructionForm: {
      icon: <IconBuildingHook className='h-[16px] w-[16px]' />,
      content: constructionForm
        ? (translate.major.constructionForm as any)[toString(constructionForm)]
        : undefined,
    },
    totalWarehouse: {
      icon: <IconBuildingWarehouse className='h-[16px] w-[16px]' />,
      content: toString(totalWarehouse),
    },
    totalWorkshops: {
      icon: <IconWarehouse className='h-[16px] w-[16px]' />,
      content: toString(totalWorkshops),
    },
    floorRange: {
      icon: <IconStairs className='h-[16px] w-[16px]' />,
      content:
        floorRange?.from && floorRange.to
          ? `${floorRange.from} - ${floorRange.to}`
          : floorRange?.from
          ? `${floorRange.from}`
          : floorRange?.to
          ? `${floorRange.to}`
          : undefined,
    },
    totalBasements: {
      icon: <IconTunnel className='h-[16px] w-[16px]' />,
      content: toString(totalBasements),
    },
    totalAreaBuild: {
      icon: <IconVectorSquareClose className='h-[16px] w-[16px]' />,
      content: totalAreaBuild?.area ? `${(totalAreaBuild?.area).toLocaleString()}` : undefined,
      suffix: 'm\u00B2',
    },
  };

  const toggleExpandAccordion = () => {
    setExpandAccordion(!isExpandAccordion);
  };

  return (
    <section id='main-content' className='rounded-[16px] bg-paper p-[24px] shadow-7'>
      <div className='flex justify-between'>
        <div className='flex items-center'>
          <div className='flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-background'>
            <IconNote className='h-[18px] w-[18px]' />
          </div>
          <h2 className='ml-[8px] text-[18px] font-[600] leading-[23px]'>
            {translate.desktop.detailProject.projectInformation}
          </h2>
        </div>
        <div
          className='flex cursor-pointer select-none items-center text-primary'
          onClick={toggleExpandAccordion}
        >
          <span className='text-[12px] leading-[15px]'>
            {isExpandAccordion ? 'Ẩn bớt' : 'Xem thêm'}
          </span>
          <IconChevronDown
            className={`ml-[6px] h-[15px] w-[15px] font-[800] ${
              isExpandAccordion && 'rotate-180'
            } duration-[200ms]`}
          />
        </div>
      </div>
      <div className={`${isExpandAccordion ? 'max-h-fit' : 'max-h-0'} overflow-hidden`}>
        <ul className='flex flex-col pt-[24px]'>
          {projectType &&
            map(
              projectTypeFields[toString(projectType)],
              (projectField, projectFieldIndex) =>
                projectInformation[projectField]?.content && (
                  <li
                    key={`project-property-${projectFieldIndex}`}
                    className='flex border-b border-stroke pb-[6px] pt-[13px]'
                  >
                    <div className='flex min-w-[150px] max-w-[150px] space-x-[13px] lg:min-w-[230px] lg:max-w-[230px] xl:min-w-[300px] xl:max-w-[300px]'>
                      {projectInformation[projectField].icon ?? null}
                      <span className='font-[600]'>
                        {(translate.desktop.detailProject as any)[projectField]}
                      </span>
                    </div>
                    <div>
                      <span
                        className={`block whitespace-pre-wrap break-words text-justify leading-[22px] ${
                          projectInformation[projectField].className ?? ''
                        }`}
                      >
                        {projectInformation[projectField].content}{' '}
                        {projectInformation[projectField].suffix}
                      </span>
                      {projectInformation[projectField].extendElement}
                    </div>
                  </li>
                ),
            )}
        </ul>
      </div>
    </section>
  );
};

export default MainContent;
